import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Plus, Pencil, Trash2, Loader, X, Search, ClipboardList,
  Calendar, Clock, BookOpen, ChevronDown, FileText, Eye, PenLine,
  Maximize2, Minimize2, BarChart2, Award, Globe, Layers, Tag,
  Target, Trophy,
} from "lucide-react";
import {
  lmsGetExams, lmsCreateExam, lmsUpdateExam, lmsDeleteExam,
  lmsGetQuestionPapers, lmsUploadQuestionPaper, lmsDeleteQuestionPaper,
  lmsCreateManualPaper, lmsGetManualPapers, lmsDeleteManualPaper,
  lmsGetAnswerSheets, lmsUploadAnswerSheet,
  lmsUnpublishQuestionPaper,
  lmsGetBatches,
} from "../api/lmsApi";
import { useSelector, useDispatch } from "react-redux";
import { fetchLmsSchoolClasses, fetchLmsClassSubjects } from "../redux/lmsSlice";
import { GetPaperMetaDataThunk } from "../../QuestionPaper/createpaper/redux/thunk";
import { getContent } from "../../QuestionPaper/createpaper/redux/selectors";
import { selectLoader } from "../../QuestionPaper/createpaper/components/others";
import "../lms.css";
import "../../QuestionPaper/css/questionPaper.css";

const getSchoolId = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}").admin_id || null; }
  catch { return null; }
};

export const EXAM_TYPES = ["unit_test", "half_yearly", "annual", "mock", "practice"];
export const EXAM_TYPE_LABELS = {
  unit_test:   "Unit Test",
  half_yearly: "Half Yearly",
  annual:      "Annual",
  mock:        "Mock",
  practice:    "Practice",
};
export const EXAM_TYPE_COLORS = {
  unit_test:   { bg: "#ede9fe", color: "#6d28d9" },
  half_yearly: { bg: "#fef9c3", color: "#854d0e" },
  annual:      { bg: "#fee2e2", color: "#991b1b" },
  mock:        { bg: "#dbeafe", color: "#1d4ed8" },
  practice:    { bg: "#dcfce7", color: "#166534" },
};

export const TAG_STYLES = {
  mock:        { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Mock" },
  unit_test:   { bg: "#faf5ff", color: "#7c3aed", border: "#e9d5ff", label: "Unit Test" },
  half_yearly: { bg: "#fffbeb", color: "#d97706", border: "#fde68a", label: "Half Yearly" },
  annual:      { bg: "#fef2f2", color: "#ef4444", border: "#fecaca", label: "Annual" },
  practice:    { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", label: "Practice" },
};

export const formatTimeStr = (t) => {
  if (!t) return "";
  try {
    const parts = t.split(":");
    let h = parseInt(parts[0], 10);
    const m = parts[1] || "00";
    if (isNaN(h)) return t;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  } catch {
    return t;
  }
};

export const getExamTimings = (exam) => {
  if (!exam) return { startDateStr: "Date TBD", startTimeFormatted: "", endDateStr: "Date TBD", endTimeFormatted: "" };

  const startDateStr = exam.exam_date
    ? new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "Date TBD";

  const startTimeFormatted = formatTimeStr(exam.start_time);

  let endDateStr = exam.end_date
    ? new Date(exam.end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : startDateStr;

  let endTimeFormatted = exam.end_time ? formatTimeStr(exam.end_time) : "";

  if (!endTimeFormatted && exam.exam_date && exam.start_time) {
    try {
      const d = new Date(exam.exam_date);
      const dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const examStart = new Date(`${dateOnly}T${exam.start_time}`);
      if (!isNaN(examStart.getTime())) {
        const durationMin = Number(exam.duration_minutes) || 50;
        const examEnd = new Date(examStart.getTime() + durationMin * 60 * 1000);
        endDateStr = examEnd.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        let h = examEnd.getHours();
        const m = String(examEnd.getMinutes()).padStart(2, "0");
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        endTimeFormatted = `${h}:${m} ${ampm}`;
      }
    } catch {
      // Fallback silently
    }
  }

  return { startDateStr, startTimeFormatted, endDateStr, endTimeFormatted };
};

function mkForm() {
  return {
    name: "", exam_type: "unit_test", mode: "offline", class_id: "", subject_id: "",
    exam_date: "", start_time: "", duration_minutes: "",
    total_marks: "", passing_marks: "", num_questions: "", instructions: "",
  };
}

/* ── Confirm Modal ── */
function ConfirmModal({ message, onConfirm, onCancel, loading }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 420, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />
        <div style={{ padding: "32px 28px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2",
            border: "2px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={26} color="#dc2626" />
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Confirm Delete</div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>{message}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, padding: "0 28px 24px", justifyContent: "center" }}>
          <button onClick={onCancel} disabled={loading} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#dc2626,#b91c1c)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 6,
          }}>
            {loading ? <Loader size={13} className="ms2-spin" /> : <Trash2 size={14} />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Field wrapper ── */
function Field({ label, children, col, error }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: col }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</label>
      {children}
      {error && (
        <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 600, marginTop: 1 }}>
          ⚠ {error}
        </span>
      )}
    </div>
  );
}
const inp = {
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
  border: "1.5px solid #e5e7eb", outline: "none", color: "#111827",
  background: "#fff", boxSizing: "border-box",
};
const inpErr = {
  ...inp,
  border: "1.5px solid #dc2626",
  background: "#fff5f5",
};

/* ── Exam Modal ── */
export function ExamModal({ exam, schoolId, classes, classSubjects, onClose, onSaved }) {
  const [form, setForm] = useState(exam ? {
    name: exam.name || "", exam_type: exam.exam_type || "unit_test",
    mode: exam.mode || "offline",
    class_id: exam.class_id ?? "", subject_id: exam.subject_id ?? "",
    batch_id: exam.batch_id ?? "",
    exam_date: exam.exam_date ? (() => { const d = new Date(exam.exam_date); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })() : "",
    start_time: exam.start_time || "", duration_minutes: exam.duration_minutes ?? "",
    total_marks: exam.total_marks ?? "", passing_marks: exam.passing_marks ?? "",
    num_questions: exam.num_questions ?? exam.total_questions ?? exam.number_of_questions ?? "", instructions: exam.instructions || "",
  } : mkForm());
  const [saving, setSaving] = useState(false);
  const [batches, setBatches] = useState([]);
  const [errors, setErrors] = useState({});
  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const filteredSubjects = classSubjects.filter(
    (cs) => String(cs.class_id) === String(form.class_id)
  );

  // Load batches when class changes
  useEffect(() => {
    if (!form.class_id || !schoolId) { setBatches([]); return; }
    lmsGetBatches({ school_id: schoolId, class_id: form.class_id })
      .then((r) => setBatches(r.data.data || []))
      .catch(() => setBatches([]));
  }, [form.class_id, schoolId]);

  const handleSave = async () => {
    const errs = {};
    if (!form.name.trim())        errs.name           = "Exam name is required";
    if (!form.class_id)           errs.class_id       = "Please select a class";
    if (!form.subject_id)         errs.subject_id     = "Please select a subject";
    if (!form.exam_date)          errs.exam_date      = "Exam date is required";
    if (!form.start_time)         errs.start_time     = "Start time is required";
    if (!form.duration_minutes || parseInt(form.duration_minutes) <= 0)
                                  errs.duration_minutes = "Duration must be greater than 0";
    if (!form.total_marks || parseInt(form.total_marks) <= 0)
                                  errs.total_marks    = "Total marks must be greater than 0";
    if (!form.passing_marks || parseInt(form.passing_marks) <= 0)
                                  errs.passing_marks  = "Passing marks must be greater than 0";
    else if (form.total_marks && parseInt(form.passing_marks) >= parseInt(form.total_marks))
                                  errs.passing_marks  = "Passing marks must be less than total marks";
    if (!form.num_questions || parseInt(form.num_questions) <= 0)
                                  errs.num_questions  = "Number of questions must be greater than 0";

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        school_id: schoolId,
        name: form.name.trim(),
        exam_type: form.exam_type,
        mode: form.mode || "offline",
        class_id: form.class_id || null,
        subject_id: form.subject_id || null,
        batch_id: form.batch_id || null,
        exam_date: form.exam_date || null,
        start_time: form.start_time || null,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
        total_marks: form.total_marks ? parseInt(form.total_marks) : null,
        passing_marks: form.passing_marks ? parseInt(form.passing_marks) : null,
        num_questions: form.num_questions ? parseInt(form.num_questions) : null,
        instructions: form.instructions || null,
      };
      if (exam) {
        await lmsUpdateExam(exam.id, payload);
        toast.success("Exam updated");
      } else {
        await lmsCreateExam(payload);
        toast.success("Exam created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save exam");
    } finally { setSaving(false); }
  };

  const tc = EXAM_TYPE_COLORS[form.exam_type] || EXAM_TYPE_COLORS.unit_test;


  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 820, maxWidth: "96vw",
        boxShadow: "0 24px 64px rgba(0,0,0,0.16)", overflow: "hidden",
        maxHeight: "92vh", display: "flex", flexDirection: "column",
      }}>
        {/* Top bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg,#4f46e5,#7c3aed,#a855f7)` }} />

        {/* Header */}
        <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12,
              background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ClipboardList size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                {exam ? "Edit Exam" : "Create New Exam"}
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Fill in the exam details below</div>
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "#f3f4f6",
            borderRadius: 8, width: 32, height: 32, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px",
          display: "flex", flexDirection: "column", gap: 10 }}>

          {/* Row 1: Exam Name + Type + Mode */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
            <Field label="Exam Name *" error={errors.name}>
              <input style={errors.name ? inpErr : inp}
                placeholder="e.g. Half Yearly Mathematics Exam"
                value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field label="Exam Type">
              <select style={inp} value={form.exam_type} onChange={(e) => set("exam_type", e.target.value)}>
                {EXAM_TYPES.map((t) => <option key={t} value={t}>{EXAM_TYPE_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field label="Mode">
              <select style={inp} value={form.mode} onChange={(e) => set("mode", e.target.value)}>
                <option value="offline">Offline</option>
                <option value="online">Online</option>
              </select>
            </Field>
          </div>

          {/* Row 2: Class + Subject + Batch */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label="Class *" error={errors.class_id}>
              <select style={errors.class_id ? inpErr : inp} value={form.class_id}
                onChange={(e) => { set("class_id", e.target.value); set("subject_id", ""); }}>
                <option value="">— Select Class —</option>
                {classes.map((c) => (
                  <option key={c.class_id ?? c.id} value={c.class_id ?? c.id}>
                    {c.class_name ?? c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Subject *" error={errors.subject_id}>
              <select style={{ ...(errors.subject_id ? inpErr : inp), opacity: form.class_id ? 1 : 0.6 }}
                value={form.subject_id} onChange={(e) => set("subject_id", e.target.value)}
                disabled={!form.class_id}>
                <option value="">— Select Subject —</option>
                {filteredSubjects.map((cs) => (
                  <option key={cs.id} value={cs.subject_id}>{cs.subject_name}</option>
                ))}
              </select>
            </Field>
            <Field label="Batch">
              <select style={{ ...inp, opacity: form.class_id ? 1 : 0.6 }}
                value={form.batch_id || ""} onChange={(e) => set("batch_id", e.target.value)}
                disabled={!form.class_id}>
                <option value="">— No specific batch —</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}{b.section ? ` (${b.section})` : ""}</option>
                ))}
              </select>
            </Field>
          </div>

          <div style={{ height: 1, background: "#f1f5f9" }} />

          {/* Row 3: Date + Time + Duration */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label="Exam Date *" error={errors.exam_date}>
              <input type="date" style={errors.exam_date ? inpErr : inp} value={form.exam_date}
                onChange={(e) => set("exam_date", e.target.value)} />
            </Field>
            <Field label="Start Time *" error={errors.start_time}>
              <input type="time" style={errors.start_time ? inpErr : inp} value={form.start_time}
                onChange={(e) => set("start_time", e.target.value)} />
            </Field>
            <Field label="Duration (minutes) *" error={errors.duration_minutes}>
              <input type="number" style={errors.duration_minutes ? inpErr : inp} placeholder="50" min="1"
                value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} />
            </Field>
          </div>

          {/* Row 4: Total Marks + Passing Marks + Num Questions */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label="Total Marks *" error={errors.total_marks}>
              <input type="number" style={errors.total_marks ? inpErr : inp} placeholder="100" min="1"
                value={form.total_marks} onChange={(e) => set("total_marks", e.target.value)} />
            </Field>
            <Field label="Passing Marks *" error={errors.passing_marks}>
              <input type="number" style={errors.passing_marks ? inpErr : inp} placeholder="35" min="1"
                value={form.passing_marks} onChange={(e) => set("passing_marks", e.target.value)} />
            </Field>
            <Field label="Number of Questions *" error={errors.num_questions}>
              <input type="number" style={errors.num_questions ? inpErr : inp} placeholder="10" min="1"
                value={form.num_questions} onChange={(e) => set("num_questions", e.target.value)} />
            </Field>
          </div>

          {/* Instructions */}
          <Field label="Instructions for Students">
            <textarea style={{ ...inp, resize: "vertical" }} rows={2}
              placeholder="e.g. All questions are compulsory. Write answers in blue ink."
              value={form.instructions} onChange={(e) => set("instructions", e.target.value)} />
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 22px", borderTop: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10,
          background: "#fafafa" }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "9px 22px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: saving ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff", cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {saving ? <Loader size={13} className="ms2-spin" /> : <Plus size={14} />}
            {exam ? "+ Save Changes" : "+ Create Exam"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Upload Paper Modal ── */
function UploadPaperModal({ examId, schoolId, onClose, onSaved }) {
  const [file, setFile]       = useState(null);
  const [title, setTitle]     = useState("");
  const [paperSet, setPaperSet] = useState("");
  const [year, setYear]       = useState(new Date().getFullYear());
  const [totalMarks, setTotalMarks] = useState("");
  const [desc, setDesc]       = useState("");
  const [saving, setSaving]   = useState(false);
  const fileRef               = React.useRef();

  const handleUpload = async () => {
    if (!file) return toast.error("Please select a PDF file");
    if (!title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("school_id", schoolId);
      fd.append("exam_id", examId);
      fd.append("title", title.trim());
      if (paperSet) fd.append("paper_set", paperSet);
      if (year)     fd.append("year", year);
      if (totalMarks) fd.append("total_marks", totalMarks);
      if (desc)     fd.append("description", desc.trim());
      await lmsUploadQuestionPaper(fd);
      toast.success("Question paper uploaded");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: "95%" }}>
        <div className="ms2-modal-header">
          <span>Upload Question Paper</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="ms2-class-modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* File picker */}
          <div className="crs-field">
            <label>PDF File *</label>
            {file ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 8 }}>
                <FileText size={16} color="#16a34a" />
                <span style={{ flex: 1, fontSize: 13, color: "#166534", overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
                <button onClick={() => setFile(null)} style={{ border: "none", background: "none",
                  cursor: "pointer", color: "#dc2626" }}><X size={14} /></button>
              </div>
            ) : (
              <div onClick={() => fileRef.current?.click()} style={{
                border: "2px dashed #d1d5db", borderRadius: 8, padding: "24px",
                textAlign: "center", cursor: "pointer", background: "#fafafa",
              }}>
                <FileText size={28} color="#9ca3af" style={{ margin: "0 auto 8px" }} />
                <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>Click to choose PDF file</p>
                <p style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 0" }}>Max 20 MB</p>
                <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }}
                  onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
              </div>
            )}
          </div>

          {/* Title + Set */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="crs-field" style={{ gridColumn: "1 / 2" }}>
              <label>Title *</label>
              <input className="crs-input" placeholder="Math Half Yearly – Set A"
                value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="crs-field">
              <label>Set</label>
              <input className="crs-input" placeholder="Set A"
                value={paperSet} onChange={(e) => setPaperSet(e.target.value)} />
            </div>
          </div>

          {/* Year + Total Marks */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="crs-field">
              <label>Year</label>
              <input type="number" className="crs-input" value={year}
                onChange={(e) => setYear(e.target.value)} />
            </div>
            <div className="crs-field">
              <label>Total Marks</label>
              <input type="number" className="crs-input" placeholder="100"
                value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)} />
            </div>
          </div>

          {/* Description */}
          <div className="crs-field">
            <label>Description</label>
            <textarea className="crs-input" rows={2} placeholder="Optional description..."
              value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
        </div>

        <div className="ms2-class-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleUpload} disabled={saving || !file}>
            {saving ? <Loader size={13} className="ms2-spin" /> : <Plus size={14} />}
            Upload Paper
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Exam Card (Admin / Teacher / Shared) ── */
export function ExamCard({ exam, onEdit, onDelete, onView, studentMode }) {
  const isOnline = exam.mode === "online";
  const tagStyle = TAG_STYLES[exam.exam_type] || TAG_STYLES.unit_test;

  const { startDateStr, startTimeFormatted, endDateStr, endTimeFormatted } = getExamTimings(exam);

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Top Accent Gradient Line */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "linear-gradient(90deg, #3b82f6 0%, #ec4899 50%, #f59e0b 100%)",
        }}
      />

      {/* Card Content Top */}
      <div>
        {/* Header Row: Icon + (Title & Tag & Subtitle) + Total Marks */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "14px" }}>
          
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flex: 1, minWidth: 0 }}>
            {/* Square Icon Box */}
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {exam.exam_type === "mock" || exam.exam_type === "practice" ? (
                <Target size={22} color="#3b82f6" />
              ) : (
                <FileText size={22} color="#3b82f6" />
              )}
            </div>

            {/* Title, Category Tag in Same Row, & Subtitle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <h3
                  title={exam.name}
                  style={{
                    fontSize: "17px",
                    fontWeight: "700",
                    color: "#0f172a",
                    margin: 0,
                    lineHeight: "1.3",
                  }}
                >
                  {exam.name}
                </h3>

                {/* Category Pill Tag in the SAME ROW as Exam Name */}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: tagStyle.bg,
                    color: tagStyle.color,
                    border: `1px solid ${tagStyle.border}`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tag size={11} />
                  {tagStyle.label}
                </span>

                {/* Mode Pill Badge */}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: isOnline ? "#dbeafe" : "#ede9fe",
                    color: isOnline ? "#1e40af" : "#6d28d9",
                    border: `1px solid ${isOnline ? "#bfdbfe" : "#ddd6fe"}`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isOnline ? <Globe size={11} /> : <FileText size={11} />}
                  {isOnline ? "Online" : "Offline"}
                </span>
              </div>

              {/* Subtitle: Class, Subject, Batch */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "13px",
                  color: "#64748b",
                  marginTop: "4px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <BookOpen size={14} color="#64748b" style={{ flexShrink: 0 }} />
                <span>
                  {exam.class_name || "Class 1"}
                  {exam.subject_name ? `, ${exam.subject_name}` : ""}
                  {exam.batch_name ? ` (${exam.batch_name})` : ""}
                </span>
              </div>
            </div>
          </div>

          {/* Right Marks Column */}
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: "20px", fontWeight: "700", color: "#3b82f6", lineHeight: 1 }}>
              {exam.total_marks || "-"}
            </div>
            <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "3px" }}>
              marks
            </div>
          </div>
        </div>

        {/* Info Box: Start Date & Time, End Date & Time, Duration */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: "12px",
            padding: "12px 14px",
            marginBottom: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            border: "1px solid #f1f5f9",
          }}
        >
          {/* Row 1: Start Date & Start Time */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#334155" }}>
              <Calendar size={13} color="#64748b" />
              <span><strong style={{ fontWeight: 600, color: "#475569" }}>Start:</strong> {startDateStr}</span>
            </div>
            {startTimeFormatted && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#334155" }}>
                <Clock size={13} color="#64748b" />
                <span>{startTimeFormatted}</span>
              </div>
            )}
          </div>

          {/* Row 2: End Date & End Time */}
          {endTimeFormatted && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#334155" }}>
                <Calendar size={13} color="#64748b" />
                <span><strong style={{ fontWeight: 600, color: "#475569" }}>End:</strong> {endDateStr}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#334155" }}>
                <Clock size={13} color="#64748b" />
                <span>{endTimeFormatted}</span>
              </div>
            </div>
          )}

          {/* Row 3: Duration & Passing marks */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#334155" }}>
              <Clock size={13} color="#64748b" />
              <span>{exam.duration_minutes ? `${exam.duration_minutes} minutes duration` : "50 minutes duration"}</span>
            </div>
            {exam.passing_marks && (
              <div style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>
                Pass: {exam.passing_marks} marks
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "auto" }}>
        <button
          type="button"
          onClick={() => onView(exam)}
          style={{
            flex: 1,
            height: "38px",
            borderRadius: "9px",
            border: "none",
            background: "#4338ca",
            color: "#ffffff",
            fontSize: "12.5px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#3730a3"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#4338ca"; }}
        >
          <Eye size={14} /> View Papers
        </button>

        {!studentMode && isOnline && (
          <button
            type="button"
            onClick={() => window.location.href = `/lms/exams/${exam.id}/results`}
            title="Online Exam Results & Visibility"
            style={{
              height: "38px",
              padding: "0 12px",
              borderRadius: "9px",
              border: "1px solid #bbf7d0",
              background: "#f0fdf4",
              color: "#16a34a",
              fontSize: "12.5px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#dcfce7"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#f0fdf4"; }}
          >
            <BarChart2 size={14} /> Results
          </button>
        )}

        {!studentMode && !isOnline && (
          <button
            type="button"
            onClick={() => window.location.href = `/lms/exams/${exam.id}/offline-results`}
            title="Offline Results & Evaluation"
            style={{
              height: "38px",
              padding: "0 12px",
              borderRadius: "9px",
              border: "1px solid #e0e7ff",
              background: "#eef2ff",
              color: "#4f46e5",
              fontSize: "12.5px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#e0e7ff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
          >
            <ClipboardList size={14} /> Evaluate
          </button>
        )}

        {!studentMode && onEdit && (
          <button
            type="button"
            onClick={() => onEdit(exam)}
            title="Edit Exam"
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "9px",
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              color: "#475569",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#ffffff"; }}
          >
            <Pencil size={14} />
          </button>
        )}

        {!studentMode && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(exam)}
            title="Delete Exam"
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "9px",
              border: "1px solid #fee2e2",
              background: "#fff5f5",
              color: "#dc2626",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff5f5"; }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Question Paper Utility Tab Content ── */
function UtilityTabContent({ exam, onClose, onNavigate, onExamUpdated, hasPdfPapers, hasManualPapers }) {
  const dispatch = useDispatch();
  const { loader } = useSelector((store) => store.createQstnPaper);
  const content = useSelector(getContent);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (exam?.question_paper_id) {
      dispatch(GetPaperMetaDataThunk({ paperId: exam.question_paper_id, type: 1 }));
    }
  }, [dispatch, exam?.question_paper_id]);

  const handleRemovePaper = async () => {
    if (!window.confirm("Are you sure you want to remove this question paper from the exam?")) return;
    setRemoving(true);
    try {
      await lmsUnpublishQuestionPaper(exam.question_paper_id || exam.id);
      toast.success("Question paper removed from exam");
      if (onExamUpdated) onExamUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove question paper");
    } finally {
      setRemoving(false);
    }
  };

  if (!exam?.question_paper_id) {
    return (
      <div style={{ textAlign: "center", padding: "48px 24px" }}>
        <div style={{
          width: 72, height: 72, borderRadius: 18, background: "#f0f0ff",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 18px",
        }}>
          <FileText size={32} color="#4f46e5" />
        </div>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 8 }}>
          No Question Paper Linked
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7, marginBottom: 24 }}>
          This exam doesn't have a question paper attached yet.
          <br />Create one from the Question Papers section.
        </div>
        <button
          onClick={() => {
            if (hasPdfPapers) {
              return toast.error("Cannot link question paper — this exam already has a PDF Upload attached. Remove it first.");
            }
            if (hasManualPapers) {
              return toast.error("Cannot link question paper — this exam already has a Manual Builder paper attached. Remove it first.");
            }
            onClose(); onNavigate("/createPaper");
          }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 14, fontWeight: 700, color: "#fff",
            background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
            border: "none", borderRadius: 10, padding: "12px 24px", cursor: "pointer",
            boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
          }}
        >
          <FileText size={16} />
          Click here to create a paper
        </button>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden",
      background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    }}>
      {/* Viewer header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", background: "#f8f9ff",
        borderBottom: "1px solid #e5e7eb", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7, background: "#ede9fe",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FileText size={14} color="#7c3aed" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>
              Question Paper Preview
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              Paper ID: {exam.question_paper_id}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={() => { onClose(); onNavigate("/qstnPapers"); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6,
              background: "#fff7ed", color: "#c2410c",
              border: "1px solid #fed7aa", cursor: "pointer",
            }}
          >
            Replace
          </button>
          <button
            onClick={handleRemovePaper}
            disabled={removing}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6,
              background: "#fef2f2", color: "#dc2626",
              border: "1px solid #fecaca", cursor: removing ? "not-allowed" : "pointer",
            }}
          >
            {removing ? <Loader size={11} className="ms2-spin" /> : <Trash2 size={11} />}
            Remove
          </button>
        </div>
      </div>

      {/* Scrollable content area */}
      <div style={{
        flex: 1, overflowY: "auto", overflowX: "auto",
        padding: "16px 14px",
        background: "#f8f9fa",
        scrollbarWidth: "thin",
        scrollbarColor: "#c7d2fe #f1f5f9",
      }}>
        {loader ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Loader size={28} className="ms2-spin" />
            <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 10 }}>
              Loading question paper...
            </p>
          </div>
        ) : content ? (
          <div
            className={`content-stage-3 ${selectLoader(loader)}`}
            dangerouslySetInnerHTML={{ __html: content }}
            style={{ width: "100%", maxWidth: "100%", margin: 0, boxSizing: "border-box" }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 13 }}>
            No question paper content available.
          </div>
        )}
      </div>
    </div>
  );
}


/* ── Exam Detail Drawer (papers list) ── */
export function ExamDetailDrawer({ exam, schoolId, onClose, onNavigate, studentMode, onExamUpdated }) {
  const [papers, setPapers]         = useState([]);
  const [manualPapers, setManualPapers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [confirmPaper, setConfirmPaper] = useState(null);
  const [confirmManual, setConfirmManual] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [creatingManual, setCreatingManual] = useState(false);
  const [activeTab, setActiveTab]   = useState("utility");


  // Answer sheet state (student mode)
  const [answerSheet,     setAnswerSheet]     = useState(null);
  const [answerFile,      setAnswerFile]      = useState(null);
  const [uploadingAnswer, setUploadingAnswer] = useState(false);
  const answerFileRef = React.useRef();

  const userData = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; } })();
  const studentId = userData?.admin_id || null;
  const [isExpanded, setIsExpanded] = useState(false);

  /* ── Paper-type exclusivity helper ── */
  const getExistingPaperType = () => {
    if (exam?.question_paper_id) return "utility";
    if (papers.length > 0)       return "pdf";
    if (manualPapers.length > 0) return "manual";
    return null;
  };

  const PAPER_TYPE_LABELS = {
    utility: "Question Paper (Utility)",
    pdf:     "PDF Upload",
    manual:  "Manual Builder",
  };

  const loadPapers = async () => {
    setLoading(true);
    try {
      const calls = [
        lmsGetQuestionPapers({ exam_id: exam.id }),
        lmsGetManualPapers({ exam_id: exam.id }),
      ];
      if (studentMode && studentId) {
        calls.push(lmsGetAnswerSheets({ exam_id: exam.id, student_id: studentId }));
      }
      const [pdfRes, manualRes, answerRes] = await Promise.all(calls);
      const pdfList    = pdfRes?.data?.data    || [];
      const manualList = manualRes?.data?.data || [];
      setPapers(pdfList);
      setManualPapers(manualList);
      if (answerRes) setAnswerSheet((answerRes?.data?.data || [])[0] || null);

      // In student mode, auto-select the tab that has content
      if (studentMode) {
        if (exam?.question_paper_id) setActiveTab("utility");
        else if (pdfList.length > 0) setActiveTab("pdf");
        else if (manualList.length > 0) setActiveTab("manual");
        else setActiveTab("utility");
      }
    } catch { setPapers([]); setManualPapers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPapers(); }, [exam.id]);

  const handleUploadAnswer = async () => {
    if (!answerFile) return toast.error("Please select a PDF file");
    if (!studentId)  return toast.error("Student ID not found");
    setUploadingAnswer(true);
    try {
      const fd = new FormData();
      fd.append("file",       answerFile);
      fd.append("school_id",  schoolId);
      fd.append("exam_id",    exam.id);
      fd.append("student_id", studentId);
      await lmsUploadAnswerSheet(fd);
      toast.success("Answer sheet uploaded successfully!");
      setAnswerFile(null);
      loadPapers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally { setUploadingAnswer(false); }
  };

  const handleCreateManual = async () => {
    const existing = getExistingPaperType();
    if (existing && existing !== "manual") {
      return toast.error(`Cannot build paper — this exam already has a ${PAPER_TYPE_LABELS[existing]} attached. Remove it first.`);
    }
    setCreatingManual(true);
    try {
      const res = await lmsCreateManualPaper({
        school_id: schoolId,
        exam_id: exam.id,
        class_id: exam.class_id || null,
        subject_id: exam.subject_id || null,
        title: exam.name,
        duration_minutes: exam.duration_minutes || null,
        total_marks: exam.total_marks || null,
        instructions: exam.instructions || null,
      });
      const newId = res?.data?.data?.id;
      if (newId) onNavigate(`/lms/manual-papers/${newId}/build`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create paper");
    } finally { setCreatingManual(false); }
  };

  const handleDeleteManual = async () => {
    try {
      await lmsDeleteManualPaper(confirmManual.id);
      toast.success("Paper deleted");
      setConfirmManual(null);
      loadPapers();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeletePaper = async () => {
    setDeleting(true);
    try {
      await lmsDeleteQuestionPaper(confirmPaper.id);
      toast.success("Paper deleted");
      setConfirmPaper(null);
      loadPapers();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };


  const tc = EXAM_TYPE_COLORS[exam.exam_type] || EXAM_TYPE_COLORS.unit_test;

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 200,
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: isExpanded ? "85vw" : 480,
        maxWidth: "96vw",
        background: "#fff", zIndex: 201, display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.15)",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}>
        {/* Header */}
        <div style={{ padding: "14px 20px 12px", borderBottom: "1px solid #e5e7eb",
          background: "#fafafa", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                  background: tc.bg, color: tc.color }}>
                  {EXAM_TYPE_LABELS[exam.exam_type]}
                </span>
                <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{exam.name}</span>
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                fontSize: 12, color: "#6b7280", marginTop: 5,
              }}>
                {(exam.class_name || exam.subject_name) && (
                  <span>
                    {exam.class_name || "—"}{exam.subject_name ? ` · ${exam.subject_name}` : ""}
                  </span>
                )}
                {exam.exam_date && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Calendar size={12} color="#9ca3af" />
                    {new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                )}
                {exam.duration_minutes && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Clock size={12} color="#9ca3af" />
                    {exam.duration_minutes} min
                  </span>
                )}
                {exam.total_marks && (
                  <span style={{ fontWeight: 600, color: "#374151" }}>
                    {exam.total_marks} marks
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                title={isExpanded ? "Collapse" : "Expand"}
                style={{
                  border: "none", background: "transparent",
                  cursor: "pointer", color: "#6b7280", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  padding: 4, borderRadius: 6,
                }}
              >
                {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button
                onClick={onClose}
                title="Close"
                style={{
                  border: "none", background: "transparent",
                  cursor: "pointer", color: "#6b7280", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  padding: 4, borderRadius: 6,
                }}
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "2px solid #e5e7eb", background: "#fafafa", flexShrink: 0 }}>
          {[
            { key: "utility", label: "Question Paper Utility" },
            { key: "pdf",    label: `PDF Upload (${papers.length})` },
            { key: "manual", label: `Manual Builder (${manualPapers.length})` },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flex: 1, padding: "10px 6px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
              background: "transparent", borderBottom: activeTab === tab.key ? "2px solid #4f46e5" : "2px solid transparent",
              color: activeTab === tab.key ? "#4f46e5" : "#9ca3af", marginBottom: -2, whiteSpace: "nowrap",
              textAlign: "center",
            }}>{tab.label}</button>
          ))}
        </div>

        {/* Papers section */}
        <div style={{
          flex: 1, overflowY: activeTab === "utility" ? "hidden" : "auto",
          padding: activeTab === "utility" ? "14px 16px" : "18px 22px",
          display: "flex", flexDirection: "column",
        }}>

          {/* ── UTILITY TAB ── */}
          {activeTab === "utility" && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <UtilityTabContent
                exam={exam}
                onClose={onClose}
                onNavigate={onNavigate}
                onExamUpdated={onExamUpdated}
                hasPdfPapers={papers.length > 0}
                hasManualPapers={manualPapers.length > 0}
              />
            </div>
          )}

          {/* ── PDF TAB ── */}
          {activeTab === "pdf" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>PDF Question Papers</div>
                {!studentMode && (
                  <button onClick={() => {
                    const existing = getExistingPaperType();
                    if (existing && existing !== "pdf") {
                      return toast.error(`Cannot upload PDF — this exam already has a ${PAPER_TYPE_LABELS[existing]} attached. Remove it first.`);
                    }
                    setShowUpload(true);
                  }} style={{
                    display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700,
                    color: "#fff", background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                    border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                  }}>
                    <Plus size={13} /> Upload PDF
                  </button>
                )}
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}><Loader size={22} className="ms2-spin" /></div>
              ) : papers.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  <FileText size={36} color="#e5e7eb" style={{ margin: "0 auto 10px" }} />
                  <p style={{ fontSize: 13 }}>{studentMode ? "No question papers available yet." : "No PDFs uploaded yet."}</p>
                  {!studentMode && (
                    <button onClick={() => {
                      const existing = getExistingPaperType();
                      if (existing && existing !== "pdf") {
                        return toast.error(`Cannot upload PDF — this exam already has a ${PAPER_TYPE_LABELS[existing]} attached. Remove it first.`);
                      }
                      setShowUpload(true);
                    }} style={{
                      display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6,
                      fontSize: 13, fontWeight: 700, color: "#fff",
                      background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                      border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer",
                    }}><Plus size={13} /> Upload First PDF</button>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {papers.map((p) => (
                    <div key={p.id} style={{
                      border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "12px 14px",
                      display: "flex", alignItems: "center", gap: 12, background: "#fafafa",
                    }}>
                      <div style={{ width: 38, height: 38, borderRadius: 8, background: "#fee2e2",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FileText size={18} color="#dc2626" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#111827",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, display: "flex", gap: 8 }}>
                          {p.paper_set && <span>Set: {p.paper_set}</span>}
                          {p.year && <span>{p.year}</span>}
                          {p.total_marks && <span>{p.total_marks} marks</span>}
                          {p.file_size && <span>{(p.file_size / (1024 * 1024)).toFixed(1)} MB</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <a href={p.file_url} target="_blank" rel="noreferrer" style={{
                          display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                          color: "#fff", background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                          borderRadius: 7, padding: "5px 10px", textDecoration: "none",
                        }}><Eye size={12} /> View</a>
                        <a href={p.file_url} download target="_blank" rel="noreferrer" style={{
                          display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                          color: "#374151", background: "#f3f4f6", border: "1.5px solid #e5e7eb",
                          borderRadius: 7, padding: "5px 10px", textDecoration: "none",
                        }}>⬇ Download</a>
                        {!studentMode && (
                          <button onClick={() => setConfirmPaper(p)} title="Delete" style={{
                            width: 30, height: 30, borderRadius: 7, border: "1.5px solid #fee2e2",
                            background: "#fff5f5", color: "#dc2626", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}><Trash2 size={12} /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── MANUAL TAB ── */}
          {activeTab === "manual" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Manual Question Papers</div>
                {!studentMode && (
                  <button onClick={handleCreateManual} disabled={creatingManual} style={{
                    display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700,
                    color: "#fff", background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
                    border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                  }}>
                    {creatingManual ? <Loader size={12} className="ms2-spin" /> : <PenLine size={13} />}
                    Build Paper
                  </button>
                )}
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}><Loader size={22} className="ms2-spin" /></div>
              ) : manualPapers.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  <PenLine size={36} color="#e5e7eb" style={{ margin: "0 auto 10px" }} />
                  <p style={{ fontSize: 13 }}>{studentMode ? "No question papers available yet." : "No papers built yet."}</p>
                  {!studentMode && (
                    <button onClick={handleCreateManual} disabled={creatingManual} style={{
                      display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6,
                      fontSize: 13, fontWeight: 700, color: "#fff",
                      background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
                      border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer",
                    }}>
                      {creatingManual ? <Loader size={12} className="ms2-spin" /> : <PenLine size={13} />}
                      Start Building
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {manualPapers.map((p) => (
                    <div key={p.id} style={{
                      border: `1.5px solid ${p.is_published ? "#86efac" : "#e5e7eb"}`,
                      borderRadius: 10, padding: "12px 14px",
                      display: "flex", alignItems: "center", gap: 12,
                      background: p.is_published ? "#f0fdf4" : "#fafafa",
                    }}>
                      <div style={{ width: 38, height: 38, borderRadius: 8, background: "#ede9fe",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <PenLine size={18} color="#7c3aed" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#111827",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, display: "flex", gap: 8 }}>
                          {p.paper_set && <span>Set: {p.paper_set}</span>}
                          <span>{p.section_count || 0} sections</span>
                          <span>{p.question_count || 0} questions</span>
                          {!studentMode && (
                            <span style={{ fontWeight: 700, color: p.is_published ? "#16a34a" : "#9ca3af" }}>
                              {p.is_published ? "✓ Published" : "Draft"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button onClick={() => onNavigate(`/lms/manual-papers/${p.id}/build`)} style={{
                          display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                          color: "#fff", background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
                          border: "none", borderRadius: 7, padding: "5px 10px", cursor: "pointer",
                        }}>{studentMode ? <Eye size={12} /> : <PenLine size={12} />} {studentMode ? "View" : "Open"}</button>
                        {!studentMode && (
                          <button onClick={() => setConfirmManual(p)} title="Delete" style={{
                            width: 30, height: 30, borderRadius: 7, border: "1.5px solid #fee2e2",
                            background: "#fff5f5", color: "#dc2626", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}><Trash2 size={12} /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Answer Upload (student mode only) ── */}
          {studentMode && (
            <div style={{ marginTop: 20, border: "1.5px solid #e0e7ff", borderRadius: 12,
              overflow: "hidden", background: "#fafbff" }}>
              <div style={{ padding: "12px 16px", background: "linear-gradient(90deg,#4f46e5,#7c3aed)",
                display: "flex", alignItems: "center", gap: 8 }}>
                <FileText size={15} color="#fff" />
                <span style={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>Submit Answer Sheet</span>
              </div>
              <div style={{ padding: "16px" }}>
                {answerSheet ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>✅</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#15803d" }}>Answer Submitted</div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{answerSheet.file_name}</div>
                      {answerSheet.marks_obtained != null && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginTop: 2 }}>
                          Marks: {answerSheet.marks_obtained} / {exam.total_marks || "—"}
                          {answerSheet.grade && ` · Grade: ${answerSheet.grade}`}
                        </div>
                      )}
                      {answerSheet.teacher_remarks && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                          Remarks: {answerSheet.teacher_remarks}
                        </div>
                      )}
                    </div>
                    <a href={answerSheet.file_url} target="_blank" rel="noreferrer" style={{
                      fontSize: 12, fontWeight: 700, color: "#4f46e5", textDecoration: "none" }}>View</a>
                  </div>
                ) : null}
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                  {answerSheet ? "Re-upload to replace your previous submission." : "Download the question paper, write your answers, then upload as PDF."}
                </div>
                <input ref={answerFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={(e) => setAnswerFile(e.target.files[0] || null)} />
                {answerFile ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                    background: "#f3f4f6", borderRadius: 8, marginBottom: 10 }}>
                    <FileText size={14} color="#4f46e5" />
                    <span style={{ fontSize: 12, flex: 1, color: "#374151" }}>{answerFile.name}</span>
                    <button onClick={() => setAnswerFile(null)} style={{ border: "none", background: "transparent",
                      cursor: "pointer", color: "#9ca3af", fontSize: 16 }}>×</button>
                  </div>
                ) : null}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => answerFileRef.current?.click()} style={{
                    flex: 1, padding: "9px 0", borderRadius: 8, border: "1.5px dashed #a5b4fc",
                    background: "#f5f3ff", color: "#4f46e5", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}>
                    {answerFile ? "Change File" : "📎 Choose File"}
                  </button>
                  {answerFile && (
                    <button onClick={handleUploadAnswer} disabled={uploadingAnswer} style={{
                      flex: 1, padding: "9px 0", borderRadius: 8, border: "none",
                      background: uploadingAnswer ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
                      color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      {uploadingAnswer ? <Loader size={13} className="ms2-spin" /> : "⬆ Submit"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showUpload && (
        <UploadPaperModal
          examId={exam.id}
          schoolId={schoolId}
          onClose={() => setShowUpload(false)}
          onSaved={loadPapers}
        />
      )}

      {confirmPaper && (
        <ConfirmModal
          message={<>Delete <strong style={{ color: "#111827" }}>"{confirmPaper.title}"</strong>?<br />The PDF file will also be removed.</>}
          onConfirm={handleDeletePaper}
          onCancel={() => setConfirmPaper(null)}
          loading={deleting}
        />
      )}

      {confirmManual && (
        <ConfirmModal
          message={<>Delete <strong style={{ color: "#111827" }}>"{confirmManual.title}"</strong>?<br />All sections and questions will be removed.</>}
          onConfirm={handleDeleteManual}
          onCancel={() => setConfirmManual(null)}
          loading={false}
        />
      )}

    </>
  );
}

/* ── Main Exams Page ── */
export default function Exams() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const schoolId  = getSchoolId();
  const teacherId = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}").teacher_id || null; } catch { return null; } })();

  const { schoolClasses, classSubjects } = useSelector((s) => s.lms);

  const [exams, setExams]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [typeFilter, setTypeFilter]   = useState("all");
  const [mainTab, setMainTab]         = useState("all"); // 'all' | 'online' | 'offline'
  const [batches, setBatches]         = useState([]);
  const [showModal, setShowModal]     = useState(false);
  const [editExam, setEditExam]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [viewExam, setViewExam]       = useState(null);

  useEffect(() => {
    dispatch(fetchLmsSchoolClasses(teacherId ? { schoolId, teacher_id: teacherId } : schoolId));
    dispatch(fetchLmsClassSubjects({ school_id: schoolId }));
  }, [schoolId]);

  useEffect(() => {
    loadExams();
  }, [schoolId, classFilter]);

  useEffect(() => {
    setBatchFilter("");
    setBatches([]);
    if (!classFilter) return;
    lmsGetBatches({ class_id: classFilter, school_id: schoolId })
      .then((r) => setBatches(r.data?.data || r.data || []))
      .catch(() => {});
  }, [classFilter, schoolId]);

  const loadExams = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = { school_id: schoolId };
      if (classFilter) params.class_id = classFilter;
      if (teacherId) params.teacher_id = teacherId;
      const res = await lmsGetExams(params);
      setExams(res?.data?.data || []);
    } catch { toast.error("Failed to load exams"); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await lmsDeleteExam(confirmDelete.id);
      toast.success("Exam deleted");
      setConfirmDelete(null);
      loadExams();
    } catch { toast.error("Failed to delete exam"); }
    finally { setDeleting(false); }
  };

  // Group exams by mode
  const onlineExams  = exams.filter((e) => e.mode === "online");
  const offlineExams = exams.filter((e) => e.mode !== "online");

  const currentPool = mainTab === "online"
    ? onlineExams
    : mainTab === "offline"
    ? offlineExams
    : exams;

  // Stats by type for the current active pool
  const typeCounts = currentPool.reduce((acc, e) => {
    acc[e.exam_type] = (acc[e.exam_type] || 0) + 1;
    return acc;
  }, {});

  const filtered = currentPool.filter((e) => {
    const matchSearch = !search ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      (e.class_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.subject_name || "").toLowerCase().includes(search.toLowerCase());
    const matchClass = !classFilter || String(e.class_id) === String(classFilter);
    const matchBatch = !batchFilter || String(e.batch_id) === String(batchFilter);
    const matchType = typeFilter === "all" || e.exam_type === typeFilter;
    return matchSearch && matchClass && matchBatch && matchType;
  });

  const statItems = Object.entries(EXAM_TYPE_LABELS).filter(([k]) => typeCounts[k]);

  const handleViewPaper = (exam) => {
    setViewExam(exam);
  };

  return (
    <div className="lms-content-inner" style={{ paddingTop: 0 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h2 className="crs-title" style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>Exams Management</h2>
          <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "2px" }}>
            Schedule online & offline tests, manage question papers, and view evaluation results
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={() => navigate("/lms/grade-configuration")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#eff6ff", color: "#1d4ed8", border: "1.5px solid #bfdbfe",
              borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            <Award size={15} /> Grade Configuration
          </button>
          <button className="lms-btn-primary" onClick={() => { setEditExam(null); setShowModal(true); }}>
            <Plus size={16} /> Create Exam
          </button>
        </div>
      </div>

      {/* Main Mode Navigation Tabs (Online, Offline, All) */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "14px",
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        {/* Tab 1: All Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("all"); setTypeFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "all" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "all" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "all" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Layers size={16} color={mainTab === "all" ? "#1c498d" : "#94a3b8"} />
          <span>All Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "all" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "all" ? "#1e40af" : "#64748b",
            }}
          >
            {exams.length}
          </span>
        </button>

        {/* Tab 2: Online Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("online"); setTypeFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "online" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "online" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "online" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <Globe size={16} color={mainTab === "online" ? "#1c498d" : "#94a3b8"} />
          <span>Online Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "online" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "online" ? "#1e40af" : "#64748b",
            }}
          >
            {onlineExams.length}
          </span>
        </button>

        {/* Tab 3: Offline Exams */}
        <button
          type="button"
          onClick={() => { setMainTab("offline"); setTypeFilter("all"); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "9px 14px",
            border: "none",
            background: "none",
            fontSize: "14px",
            fontWeight: mainTab === "offline" ? "700" : "600",
            cursor: "pointer",
            color: mainTab === "offline" ? "#1c498d" : "#64748b",
            borderBottom: mainTab === "offline" ? "3px solid #1c498d" : "3px solid transparent",
            marginBottom: "-2px",
            transition: "all 0.15s ease",
          }}
        >
          <FileText size={16} color={mainTab === "offline" ? "#1c498d" : "#94a3b8"} />
          <span>Offline Exams</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              padding: "2px 7px",
              borderRadius: "10px",
              background: mainTab === "offline" ? "#dbeafe" : "#f1f5f9",
              color: mainTab === "offline" ? "#1e40af" : "#64748b",
            }}
          >
            {offlineExams.length}
          </span>
        </button>
      </div>

      {/* Filter bar */}
      <div className="crs-filters" style={{ marginBottom: 14 }}>
        <div className="crs-search-wrap" style={{ maxWidth: 260 }}>
          <Search size={15} className="crs-search-icon" />
          <input className="crs-search" placeholder="Search exams by name, class, subject..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="crs-filter-select" value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}>
          <option value="">All Classes</option>
          {(schoolClasses || []).map((c) => (
            <option key={c.class_id ?? c.id} value={c.class_id ?? c.id}>
              {c.class_name ?? c.name}
            </option>
          ))}
        </select>
        <select className="crs-filter-select" value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          disabled={!classFilter} style={{ opacity: classFilter ? 1 : 0.5 }}>
          <option value="">All Batches</option>
          {batches.map((b) => (
            <option key={b.id} value={String(b.id)}>{b.name}{b.section ? ` (${b.section})` : ""}</option>
          ))}
        </select>
        <select className="crs-filter-select" value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {EXAM_TYPES.map((t) => <option key={t} value={t}>{EXAM_TYPE_LABELS[t]}</option>)}
        </select>
      </div>

      {/* Category Filter Chips */}
      {statItems.length > 0 && (
        <div style={{ display: "flex", gap: "7px", marginBottom: "14px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setTypeFilter("all")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "4px 12px",
              borderRadius: "18px",
              fontSize: "12px",
              fontWeight: "700",
              cursor: "pointer",
              border: typeFilter === "all" ? "1.5px solid #1c498d" : "1px solid #e2e8f0",
              background: typeFilter === "all" ? "#eff6ff" : "#ffffff",
              color: typeFilter === "all" ? "#1c498d" : "#64748b",
              transition: "all 0.15s ease",
            }}
          >
            <BarChart2 size={12} /> All ({currentPool.length})
          </button>
          {statItems.map(([key, label]) => {
            const tc = EXAM_TYPE_COLORS[key] || EXAM_TYPE_COLORS.unit_test;
            const active = typeFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTypeFilter(active ? "all" : key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 12px",
                  borderRadius: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: active ? `1.5px solid ${tc.color}` : "1px solid #e2e8f0",
                  background: active ? tc.bg : "#ffffff",
                  color: active ? tc.color : "#64748b",
                  transition: "all 0.15s ease",
                }}
              >
                {label} ({typeCounts[key]})
              </button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Loader size={28} className="ms2-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af", background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
          <ClipboardList size={48} color="#cbd5e1" style={{ margin: "0 auto 16px" }} />
          <p style={{ fontSize: 16, fontWeight: 700, color: "#374151" }}>No exams found</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>
            {search || typeFilter !== "all" || classFilter ? "Try adjusting your search query or filters." : "Click \"+ Create Exam\" to schedule your first exam."}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
            gap: "20px",
          }}
        >
          {filtered.map((exam) => (
            <ExamCard
              key={exam.id}
              exam={exam}
              onEdit={(e) => { setEditExam(e); setShowModal(true); }}
              onDelete={(e) => setConfirmDelete(e)}
              onView={(e) => handleViewPaper(e)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ExamModal
          exam={editExam}
          schoolId={schoolId}
          classes={schoolClasses || []}
          classSubjects={classSubjects || []}
          onClose={() => { setShowModal(false); setEditExam(null); }}
          onSaved={loadExams}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          message={<>Are you sure you want to delete <strong style={{ color: "#111827" }}>"{confirmDelete.name}"</strong>?<br />All linked question papers will also be deleted.</>}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
          loading={deleting}
        />
      )}

      {viewExam && (
        <ExamDetailDrawer
          exam={viewExam}
          schoolId={schoolId}
          onClose={() => setViewExam(null)}
          onNavigate={(path) => { setViewExam(null); navigate(path); }}
          onExamUpdated={() => {
            loadExams();
            setViewExam((prev) => (prev ? { ...prev, question_paper_id: null } : null));
          }}
        />
      )}
    </div>
  );
}
